import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './Scheduler.css';

const DAYS = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DEFAULT_BROADCAST_TIMES = ['08:00', '11:00', '15:00', '20:00', '23:00'];

const VOICES = [
  { id: 'nl_NL-pim-medium', name: 'Pim (Man, Nederlands)' },
  { id: 'nl_NL-ronnie-medium', name: 'Ronnie (Man, Energiek)' },
  { id: 'nl_BE-nathalie-medium', name: 'Nathalie (Vrouw, Belgisch)' },
  { id: 'nl_BE-rdh-medium', name: 'Ruben (Man, Belgisch)' },
  { id: 'nl_NL-mls-medium', name: 'MLS (Gemengd)' }
];

function Scheduler() {
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newWeatherTime, setNewWeatherTime] = useState('08:00');
  const [formData, setFormData] = useState({
    name: '',
    day_of_week: 0,
    start_time: '00:00:00',
    end_time: '24:00:00',
    voice_id: 'nl_NL-pim-medium',
    auto_news_enabled: true,
    news_broadcast_times: DEFAULT_BROADCAST_TIMES,
    auto_weather_enabled: true,
    weather_broadcast_times: DEFAULT_BROADCAST_TIMES,
    enabled: true
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await api.get('/schedules');
      setSchedules(response.data);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const handleCellClick = (day, hour) => {
    setFormData({
      name: `${DAYS[day]} ${hour}:00`,
      day_of_week: day,
      start_time: `${hour.toString().padStart(2, '0')}:00:00`,
      end_time: `${((hour + 1) % 24).toString().padStart(2, '0')}:00:00`,
      voice_id: 'nl_NL-pim-medium',
      auto_news_enabled: true,
      news_broadcast_times: DEFAULT_BROADCAST_TIMES,
      auto_weather_enabled: true,
      weather_broadcast_times: DEFAULT_BROADCAST_TIMES,
      enabled: true
    });
    setSelectedSchedule(null);
    setShowModal(true);
  };

  const handleScheduleClick = (schedule) => {
    setFormData({
      name: schedule.name,
      day_of_week: schedule.day_of_week,
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      voice_id: schedule.voice_id,
      auto_news_enabled: schedule.auto_news_enabled !== undefined ? schedule.auto_news_enabled : true,
      news_broadcast_times: schedule.news_broadcast_times || DEFAULT_BROADCAST_TIMES,
      auto_weather_enabled: schedule.auto_weather_enabled !== undefined ? schedule.auto_weather_enabled : true,
      weather_broadcast_times: schedule.weather_broadcast_times || DEFAULT_BROADCAST_TIMES,
      enabled: schedule.enabled
    });
    setSelectedSchedule(schedule);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (selectedSchedule) {
        // Update existing
        await api.put(`/schedules/${selectedSchedule.id}`, formData);
      } else {
        // Create new
        await api.post('/schedules', formData);
      }
      fetchSchedules();
      setShowModal(false);
      setSelectedSchedule(null);
    } catch (error) {
      console.error('Failed to save schedule:', error);
      alert('Fout bij opslaan van schema');
    }
  };

  const handleDelete = async () => {
    if (!selectedSchedule) return;
    if (!window.confirm(`Weet je zeker dat je "${selectedSchedule.name}" wilt verwijderen?`)) return;

    try {
      await api.delete(`/schedules/${selectedSchedule.id}`);
      fetchSchedules();
      setShowModal(false);
      setSelectedSchedule(null);
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      alert('Fout bij verwijderen van schema');
    }
  };

  const handleToggle = async (schedule) => {
    try {
      await api.patch(`/schedules/${schedule.id}/toggle`);
      fetchSchedules();
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
    }
  };

  const getScheduleForCell = (day, hour) => {
    return schedules.find(s => {
      if (s.day_of_week !== day || !s.enabled) return false;
      const cellTime = `${hour.toString().padStart(2, '0')}:00:00`;
      const startTime = s.start_time;
      const endTime = s.end_time === '00:00:00' ? '24:00:00' : s.end_time;
      return startTime <= cellTime && endTime > cellTime;
    });
  };

  const timeToHour = (timeStr) => {
    return parseInt(timeStr.split(':')[0]);
  };

  const getScheduleHeight = (schedule) => {
    const startHour = timeToHour(schedule.start_time);
    const endHour = timeToHour(schedule.end_time);
    const hours = endHour > startHour ? endHour - startHour : (24 - startHour) + endHour;
    return hours;
  };

  return (
    <div className="scheduler-container">
      <div className="scheduler-header">
        <h2>📅 Weekkalender</h2>
        <p>Klik op een tijdslot om een schema toe te voegen</p>
      </div>

      <div className="calendar-grid">
        <div className="calendar-header">
          <div className="time-column-header">Tijd</div>
          {DAYS.map((day, idx) => (
            <div key={idx} className="day-header">{day}</div>
          ))}
        </div>

        <div className="calendar-body">
          <div className="time-column">
            {HOURS.map(hour => (
              <div key={hour} className="time-cell">
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {DAYS.map((day, dayIdx) => (
            <div key={dayIdx} className="day-column">
              {HOURS.map(hour => {
                const schedule = getScheduleForCell(dayIdx, hour);
                const isScheduleStart = schedule && timeToHour(schedule.start_time) === hour;
                
                return (
                  <div
                    key={hour}
                    className={`hour-cell ${schedule ? 'has-schedule' : ''}`}
                    onClick={() => !schedule && handleCellClick(dayIdx, hour)}
                  >
                    {isScheduleStart && (
                      <div
                        className="schedule-block"
                        style={{ height: `${getScheduleHeight(schedule) * 50}px` }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleScheduleClick(schedule);
                        }}
                      >
                        <div className="schedule-name">{schedule.name}</div>
                        <div className="schedule-voice">
                          🎤 {VOICES.find(v => v.id === schedule.voice_id)?.name || schedule.voice_id}
                        </div>
                        <div className="schedule-time">
                          {schedule.start_time.slice(0, 5)} - {schedule.end_time.slice(0, 5)}
                        </div>
                        {schedule.auto_news_enabled && (
                          <div className="schedule-feature">
                            📰 Nieuws + 🌤️ Weer: {(schedule.news_broadcast_times || DEFAULT_BROADCAST_TIMES).join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Modal for adding/editing schedule */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedSchedule ? 'Schema Bewerken' : 'Nieuw Schema'}</h3>

            <div className="form-group">
              <label>Naam:</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Dag:</label>
                <select
                  value={formData.day_of_week}
                  onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                >
                  {DAYS.map((day, idx) => (
                    <option key={idx} value={idx}>{day}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Start Tijd:</label>
                <input
                  type="time"
                  value={formData.start_time.slice(0, 5)}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value + ':00' })}
                />
              </div>

              <div className="form-group">
                <label>Eind Tijd:</label>
                <input
                  type="time"
                  value={formData.end_time.slice(0, 5)}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value + ':00' })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Stem:</label>
              <select
                value={formData.voice_id}
                onChange={(e) => setFormData({ ...formData, voice_id: e.target.value })}
              >
                {VOICES.map(voice => (
                  <option key={voice.id} value={voice.id}>{voice.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.auto_news_enabled}
                  onChange={(e) => setFormData({ ...formData, auto_news_enabled: e.target.checked })}
                />
                Automatisch Nieuws
              </label>
              {formData.auto_news_enabled && (
                <input
                  type="number"
                  min="15"
                  max="360"
                  value={formData.auto_news_interval}
                  onChange={(e) => setFormData({ ...formData, auto_news_interval: parseInt(e.target.value) })}
                  placeholder="Interval (minuten)"
                />
              )}
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.auto_weather_enabled}
                  onChange={(e) => setFormData({ ...formData, auto_weather_enabled: e.target.checked })}
                />
                Automatisch Weerbericht
              </label>
              {formData.auto_weather_enabled && (
                <div className="weather-times-manager">
                  <p style={{ margin: '6px 0 4px', fontSize: '13px', color: '#aaa' }}>Tijden (HH:MM) waarop het weerbericht wordt uitgezonden:</p>
                  <div className="weather-times-list">
                    {(formData.weather_broadcast_times || []).map((time, idx) => (
                      <span key={idx} className="time-badge">
                        {time}
                        <button
                          type="button"
                          className="time-badge-remove"
                          onClick={() => setFormData({ ...formData, weather_broadcast_times: formData.weather_broadcast_times.filter((_, i) => i !== idx) })}
                        >×</button>
                      </span>
                    ))}
                  </div>
                  <div className="add-time-row">
                    <input
                      type="time"
                      value={newWeatherTime}
                      onChange={(e) => setNewWeatherTime(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn-add-time"
                      onClick={() => {
                        if (newWeatherTime && !(formData.weather_broadcast_times || []).includes(newWeatherTime)) {
                          setFormData({ ...formData, weather_broadcast_times: [...(formData.weather_broadcast_times || []), newWeatherTime].sort() });
                        }
                      }}
                    >+ Toevoegen</button>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                />
                Actief
              </label>
            </div>

            <div className="modal-actions">
              <button onClick={handleSave} className="btn-save">Opslaan</button>
              {selectedSchedule && (
                <button onClick={handleDelete} className="btn-delete">Verwijderen</button>
              )}
              <button onClick={() => setShowModal(false)} className="btn-cancel">Annuleren</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Scheduler;
